import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { hoursToMinutes } from 'date-fns';
import { BehaviorSubject, Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { getNewUnixTimestamp } from 'server/shared/helpers/removeTimeZoneOffset';
import {
  getTimeTrackingTotal,
  minutesToMilitaryTime,
} from 'server/shared/helpers/time';
import { Customer } from 'server/shared/models/customer';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { Shift } from 'server/shared/models/shift';
import { TimeTracking } from 'server/shared/models/timeTracking';
import { User } from 'server/shared/models/user';
import SignaturePad from 'signature_pad';
import { customValidator } from 'src/helpers/timeTrackingHelper';
import { TimeTrackingService } from '../../../services/time-tracking.service';
import { ToastService } from '../../../services/toast/toast.service';
import { TranslationService } from '../../../services/translation.service';
import { ConfirmationDialog } from '../../confirmation-dialog/confirmation.component';

export type TimeTrackingWithFormGroup = {
  id: string;
  timeTracking: TimeTracking;
  formGroup: FormGroup;
};
@Component({
  selector: 'app-time-tracking-stepper',
  templateUrl: 'time-tracking-stepper.component.html',
  styleUrls: ['time-tracking-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeTrackingStepperComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('signatureCanvas', { static: false })
  signatureCanvas?: ElementRef<HTMLCanvasElement>;

  private destroyed$ = new Subject<void>();
  private signaturePad?: SignaturePad;

  // Outputs
  @Output() closeSheet = new EventEmitter<void>();
  @Output() timeTrackingSubmitted = new EventEmitter<
    TimeTrackingWithFormGroup[]
  >();
  @Output() signatureSubmitted = new EventEmitter<{
    name: string;
    signatureFile: {
      fileName: string;
      fileType: string;
      fileData: string;
    };
    timeTrackings: TimeTrackingWithFormGroup[];
  }>();

  // Signature state
  signatureDataUrl$ = new BehaviorSubject<string | null>(null);
  hasSignature$ = new BehaviorSubject<boolean>(false);

  // Timer state (1 hour countdown)
  private readonly INITIAL_TIME_SECONDS = 3599; // 1 hour
  timeRemaining$ = new BehaviorSubject<number>(this.INITIAL_TIME_SECONDS);
  timerExpired$ = new BehaviorSubject<boolean>(false);

  // Inputs
  invitee$ = new BehaviorSubject<Invitee | undefined>(undefined);
  @Input() set invitee(value: Invitee | undefined) {
    this.invitee$.next(value);
    // Update timer when invitee changes
    this.updateTimer();
  }
  get invitee() {
    return this.invitee$.value;
  }

  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(value: Job | undefined) {
    this.job$.next(value);
  }
  get job() {
    return this.job$.value;
  }

  @Input() magicSecret?: string;

  timeTrackingValues$ = new BehaviorSubject<TimeTrackingWithFormGroup[]>([]);
  @Input() set timeTrackingValues(
    value: TimeTrackingWithFormGroup[] | undefined,
  ) {
    value
      ?.filter((item) => item.formGroup.disabled)
      .forEach((item) => {
        item.formGroup.enable();
      });

    const values = value || [];
    this.timeTrackingValues$.next(values);
    this.updateTimeTrackingForm(values);
    this.updateTimeTrackingFormStep0(values);
    // Update timer when values change
    this.updateTimer();
    // Update total when values change
    this.updateTimeTrackingTotal();
  }
  get timeTrackingValues() {
    return this.timeTrackingValues$.value;
  }

  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  get customer() {
    return this.customer$.value;
  }

  loggedInUser?: User;
  hasChanged: boolean = false;

  i18n = this.translationService.getTranslations('time-tracking');

  previousName?: string;

  timeTrackingTotal$ = new BehaviorSubject<number>(0);
  get timeTrackingTotal() {
    return this.timeTrackingTotal$.value;
  }

  step0FormGroup: FormGroup;
  step3FormGroup: FormGroup;
  step4FormGroup: FormGroup;

  // Time tracking forms array for step 0 and step 3
  timeTrackingFormsArray: FormArray;
  timeTrackingFormsArrayStep0: FormArray;
  timeTrackingsWithFormGroup$ = new BehaviorSubject<
    TimeTrackingWithFormGroup[]
  >([]);

  isSubmitting = false;

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private toastService: ToastService,
    private timeTrackingService: TimeTrackingService,
  ) {
    // Initialize time tracking forms array
    this.timeTrackingFormsArray = this.formBuilder.array([]);
    this.timeTrackingFormsArrayStep0 = this.formBuilder.array(
      [],
      customValidator.bind(this),
    );

    this.step0FormGroup = this.formBuilder.group({
      timeTrackings: this.timeTrackingFormsArrayStep0,
    });
    this.step3FormGroup = this.formBuilder.group({
      timeTrackings: this.timeTrackingFormsArray,
    });
    this.step4FormGroup = this.formBuilder.group({
      name: ['', Validators.required],
      signature: ['', Validators.required],
    });
  }

  private trackerComputation?: Tracker.Computation;

  ngOnInit() {
    // Start the countdown timer
    this.startCountdownTimer();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.trackerComputation) {
      this.trackerComputation = Tracker.autorun(() => {
        this.loggedInUser = Users.findOne(Meteor.userId());
        const customer = Customers.findOne(this.invitee?.customer.id);
        this.ngZone.run(() => {
          this.customer$.next(customer);
        });
      });
    }
  }

  ngAfterViewInit() {
    // Initialize signature pad after view init
    this.initializeSignaturePad();
  }

  ngOnDestroy() {
    if (this.trackerComputation) {
      this.trackerComputation.stop();
    }
    if (this.signaturePad) {
      this.signaturePad.off();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  trackById(_index: number, item: TimeTrackingWithFormGroup) {
    return item.id;
  }

  goNext() {
    if (this.stepper) {
      this.stepper.next();
    }
  }

  goBack() {
    if (this.stepper) {
      this.stepper.previous();
    }
  }

  isLastStep(): boolean {
    const hasTimeTrackingStep = !this.timeTrackingsWithFormGroup$.value.some(
      (tracking) => !!tracking.timeTracking.toBeApprovedAt,
    );
    // If step 0 exists, last step is 5, otherwise it's 4
    return hasTimeTrackingStep
      ? this.stepper?.selectedIndex === 5
      : this.stepper?.selectedIndex === 4;
  }

  isFirstStep(): boolean {
    return this.stepper?.selectedIndex === 0;
  }

  isTimeTrackingStep(): boolean {
    const hasTimeTrackingStep = !this.timeTrackingsWithFormGroup$.value.some(
      (tracking) => !!tracking.timeTracking.toBeApprovedAt,
    );
    return hasTimeTrackingStep && this.stepper?.selectedIndex === 0;
  }

  isSignatureStep(): boolean {
    const hasTimeTrackingStep = !this.timeTrackingsWithFormGroup$.value.some(
      (tracking) => !!tracking.timeTracking.toBeApprovedAt,
    );
    // If step 0 exists, signature step is 4, otherwise it's 3
    return hasTimeTrackingStep
      ? this.stepper?.selectedIndex === 4
      : this.stepper?.selectedIndex === 3;
  }

  isCurrentStepInvalid(): boolean {
    if (!this.stepper?.selected) {
      return false;
    }
    const stepControl = this.stepper.selected.stepControl;
    return stepControl ? stepControl.invalid : false;
  }

  close() {
    this.closeSheet.emit();
  }

  // Timer methods
  private startCountdownTimer() {
    // Update timer every second
    interval(1000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateTimer();
      });

    // Update immediately on start
    this.updateTimer();
  }

  private updateTimer() {
    const invitee = this.invitee$.value;
    if (!invitee) {
      this.timeRemaining$.next(this.INITIAL_TIME_SECONDS);
      return;
    }

    if (!invitee.timeTrackings?.some((item) => item.toBeApprovedAt)) {
      // If not set yet, show full hour
      this.timeRemaining$.next(this.INITIAL_TIME_SECONDS);
      return;
    }

    let toBeApprovedAt =
      invitee.timeTrackings?.find((item) => item.toBeApprovedAt)
        ?.toBeApprovedAt || 0;

    let differenceToNow = Math.floor(
      (Date.now() - toBeApprovedAt * 1000) / 1000,
    );

    if (differenceToNow > 3600) {
      this.timerExpired$.next(true);
      this.timeRemaining$.next(0);
    } else {
      this.timeRemaining$.next(3600 - differenceToNow);
    }

    this.cdr.detectChanges();
  }

  getFormattedTimeRemaining(): string {
    const totalSeconds = this.timeRemaining$.value;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Format as MM:SS
    const minutesStr = minutes.toString().padStart(2, '0');
    const secondsStr = seconds.toString().padStart(2, '0');

    return `${minutesStr}:${secondsStr}`;
  }

  continueAfterSignature() {
    if (this.step4FormGroup.invalid || !this.signaturePad) {
      this.step4FormGroup.markAllAsTouched();
      return;
    }

    this.submitSignature();
  }

  submitSignature() {
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    const name = this.step4FormGroup.get('name')?.value;

    // Convert signature to JPEG
    const signatureJpeg = this.convertSignatureToJpeg();

    if (!signatureJpeg) {
      console.error('Failed to convert signature to JPEG');
      this.isSubmitting = false;
      this.goBack();
      return;
    }

    // Emit the signature data
    this.signatureSubmitted.emit({
      name,
      signatureFile: signatureJpeg,
      timeTrackings: this.timeTrackingsWithFormGroup$.value,
    });
    this.goNext();
  }

  private convertSignatureToJpeg(): {
    fileName: string;
    fileType: string;
    fileData: string;
  } | null {
    if (!this.signaturePad || !this.signatureCanvas) {
      return null;
    }

    try {
      const canvas = this.signatureCanvas.nativeElement;

      // Create a temporary canvas with white background
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const ctx = tempCanvas.getContext('2d');

      if (!ctx) {
        return null;
      }

      // Fill with white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw the signature on top
      ctx.drawImage(canvas, 0, 0);

      // Convert to JPEG with 0.9 quality
      const jpegDataUrl = tempCanvas.toDataURL('image/jpeg', 0.9);

      // Extract base64 data (remove the data:image/jpeg;base64, prefix)
      const base64Data = jpegDataUrl.split(',')[1];

      // Generate filename with timestamp
      const timestamp = new Date().getTime();
      const fileName = `signature_${timestamp}.jpg`;

      return {
        fileName,
        fileType: 'image/jpeg',
        fileData: base64Data,
      };
    } catch (error) {
      console.error('Error converting signature to JPEG:', error);
      return null;
    }
  }

  // Time tracking form methods
  private updateTimeTrackingForm(
    timeTrackingsWithFormGroup: TimeTrackingWithFormGroup[],
  ) {
    if (
      !timeTrackingsWithFormGroup ||
      timeTrackingsWithFormGroup.length === 0
    ) {
      return;
    }

    // Ensure form array is initialized
    if (!this.timeTrackingFormsArray) {
      return;
    }

    // Clear existing form array
    this.timeTrackingFormsArray.clear();

    // Add the existing form groups to the form array
    timeTrackingsWithFormGroup.forEach((item) => {
      this.timeTrackingFormsArray.push(item.formGroup);
    });

    // Update the observable
    this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
    this.cdr.detectChanges();
  }

  // Signature pad methods
  private initializeSignaturePad() {
    if (!this.signatureCanvas) {
      // Retry after a short delay if canvas not ready yet
      setTimeout(() => this.initializeSignaturePad(), 100);
      return;
    }

    const canvas = this.signatureCanvas.nativeElement;

    // Set canvas size
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);

    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
    });

    // Listen for signature changes
    this.signaturePad.addEventListener('endStroke', () => {
      this.onSignatureChange();
    });

    this.cdr.detectChanges();
  }

  private onSignatureChange() {
    if (!this.signaturePad) return;

    const isEmpty = this.signaturePad.isEmpty();
    this.hasSignature$.next(!isEmpty);

    if (!isEmpty) {
      const dataUrl = this.signaturePad.toDataURL();
      this.signatureDataUrl$.next(dataUrl);
      this.step4FormGroup.patchValue({ signature: dataUrl });
      this.step4FormGroup.markAsTouched();
    } else {
      this.signatureDataUrl$.next(null);
      this.step4FormGroup.patchValue({ signature: '' });
    }

    this.cdr.detectChanges();
  }

  clearSignature() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.hasSignature$.next(false);
      this.signatureDataUrl$.next(null);
      this.step4FormGroup.patchValue({ signature: '' });
      this.step4FormGroup.markAsUntouched();
      this.cdr.detectChanges();
    }
  }

  resizeCanvas() {
    if (!this.signatureCanvas || !this.signaturePad) return;

    const canvas = this.signatureCanvas.nativeElement;
    const data = this.signaturePad.toData();

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);

    this.signaturePad.fromData(data);
  }

  updateTimeValue(
    value: string,
    index: number,
    reference: keyof Pick<
      TimeTracking,
      | 'from'
      | 'correctedFrom'
      | 'to'
      | 'correctedTo'
      | 'pause'
      | 'correctedPause'
    >,
  ) {
    this.hasChanged = true;
    this.ngZone.run(() => {
      const timeTrackingsWithFormGroup = [
        ...this.timeTrackingsWithFormGroup$.value,
      ];
      timeTrackingsWithFormGroup[index].timeTracking[reference] =
        Number.parseInt(value.replace(':', ''), 10) || 0;
      this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
      this.updateTimeTrackingTotal();
    });
  }

  sanitizePauseInput(value: string): string {
    let result = '';
    let hasColon = false;

    for (const char of value) {
      if (char >= '0' && char <= '9') {
        result += char;
      } else if (char === ':' && !hasColon) {
        result += ':';
        hasColon = true;
      }
    }

    return result;
  }

  parsePauseInput(raw: string): string {
    const sanitized = raw.trim();
    if (!sanitized) {
      return '00:00';
    }

    const colonIndex = sanitized.indexOf(':');

    if (colonIndex === -1) {
      const totalMinutes = Number.parseInt(sanitized, 10) || 0;
      return this.formatPauseDuration(
        Math.floor(totalMinutes / 60),
        totalMinutes % 60,
      );
    }

    const beforeColon = sanitized.slice(0, colonIndex);
    const afterColon = sanitized.slice(colonIndex + 1);

    if (!beforeColon) {
      const totalMinutes = Number.parseInt(afterColon, 10) || 0;
      return this.formatPauseDuration(
        Math.floor(totalMinutes / 60),
        totalMinutes % 60,
      );
    }

    let hours = Number.parseInt(beforeColon, 10) || 0;
    let minutes = Number.parseInt(afterColon, 10) || 0;
    if (minutes > 59) {
      hours += Math.floor(minutes / 60);
      minutes = minutes % 60;
    }

    return this.formatPauseDuration(hours, minutes);
  }

  private formatPauseDuration(hours: number, minutes: number): string {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  }

  onPauseInput(
    event: Event,
    index: number,
    reference: 'pause' | 'correctedPause',
  ) {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizePauseInput(input.value);
    const formGroup = this.timeTrackingsWithFormGroup$.value[index].formGroup;

    if (sanitized !== input.value) {
      formGroup.controls['pause'].setValue(sanitized, { emitEvent: false });
    }

    this.updateTimeValue(this.parsePauseInput(sanitized), index, reference);
  }

  onPauseBlur(
    event: Event,
    index: number,
    reference: 'pause' | 'correctedPause',
  ) {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizePauseInput(input.value);
    const formatted = this.parsePauseInput(sanitized);
    const formGroup = this.timeTrackingsWithFormGroup$.value[index].formGroup;

    formGroup.controls['pause'].setValue(formatted, { emitEvent: false });
    this.updateTimeValue(formatted, index, reference);
  }

  /**
   * Updates the correction reason for a time tracking entry
   */
  updateCorrectionReason(value: string, index: number) {
    const timeTrackingsWithFormGroup = [
      ...this.timeTrackingsWithFormGroup$.value,
    ];
    timeTrackingsWithFormGroup[index].timeTracking.correctionReason = value;
    this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
  }

  /**
   * Checks if a time tracking entry has changes from original shift values
   */
  displayCorrectionReason(
    timeTracking: TimeTracking,
    originalShift?: Shift,
  ): boolean {
    const originalTime = {
      from: originalShift?.from,
      to: originalShift?.to,
    };
    const originalPause =
      this.customer?.timeTrackingDefaultPauseByDepartment?.[
        this.job?.department.id || ''
      ]?.[timeTracking.shift] !== undefined
        ? this.customer?.timeTrackingDefaultPauseByDepartment?.[
            this.job?.department.id || ''
          ]?.[timeTracking.shift]
        : this.customer?.timeTrackingDefaultPauseInHours?.[
            timeTracking.shift
          ] || 0;
    const originalPauseMinutes = hoursToMinutes(originalPause || 0);
    const originalPauseMilitaryTime =
      minutesToMilitaryTime(originalPauseMinutes);

    const showReasonForPauseChange =
      originalPause !== undefined &&
      timeTracking.pause !== originalPauseMilitaryTime;
    return (
      timeTracking.from !== originalTime.from ||
      timeTracking.to !== originalTime.to ||
      showReasonForPauseChange ||
      !!timeTracking.correctionReason
    );
  }

  // Time tracking submission methods (similar to time-tracking.component.ts for POOL role)
  setToBeApproved(event: Event) {
    event.stopPropagation();
    if (!this.job || !this.invitee) return;
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: this.job._id,
        text:
          this.i18n?.transferConfirmationText ||
          'Möchtest du die Stunden wirklich abschliessen? Die Stunden können danach nicht mehr bearbeitet werden.',
        showNameInput: false,
        defaultImpolite: true,
      },
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        let now = getNewUnixTimestamp();
        this.ngZone.run(() => {
          if (!this.job || !this.invitee) return;
          const timeTrackingsWithFormGroup = [
            ...this.timeTrackingsWithFormGroup$.value,
          ];
          timeTrackingsWithFormGroup.forEach((timeTracking) => {
            // Sync correctionReason from form group before saving
            const correctionReasonControl =
              timeTracking.formGroup.get('correctionReason');
            if (correctionReasonControl) {
              timeTracking.timeTracking.correctionReason =
                correctionReasonControl.value || undefined;
            }
            timeTracking.timeTracking.toBeApprovedAt = now;
            timeTracking.formGroup.disable();
          });
          this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
          // Update both form arrays
          this.updateTimeTrackingForm(timeTrackingsWithFormGroup);
          this.updateTimeTrackingFormStep0(timeTrackingsWithFormGroup);

          const hasSignatureProcessEnabled = timeTrackingsWithFormGroup.some(
            (tracking) => !!tracking.timeTracking.signatureProcessEnabled,
          );

          this.timeTrackingService.saveTimeTracking({
            jobId: this.job._id,
            timeTrackings: this.timeTrackingsWithFormGroup$.value.map(
              (t) => t.timeTracking,
            ),
            inviteeId: this.invitee.id,
            userId: this.magicSecret ? this.invitee.userId ?? null : null,
            externalLinkValidatorData: this.magicSecret
              ? { secret: this.magicSecret }
              : undefined,
            callback: (error: Meteor.Error) => {
              if (!error) {
                this.toastService.add({
                  type: 'SUCCESS',
                  message: 'hoursTransferred',
                });
              } else {
                console.error('error: ', error);
                this.toastService.add({
                  type: 'ERROR',
                  message: 'serverError',
                });
              }
            },
          });
          this.timeTrackingSubmitted.emit(timeTrackingsWithFormGroup);

          if (!hasSignatureProcessEnabled) {
            this.close();
            return;
          }

          setTimeout(() => {
            if (this.stepper) {
              this.stepper.selectedIndex = 0;
              this.cdr.detectChanges();
            }
          }, 0);
        });
      }
    });
  }

  getTimeTrackingTotal(): number {
    if (!this.job) return 0;
    return getTimeTrackingTotal(
      this.timeTrackingsWithFormGroup$.value.map((t) => t.timeTracking),
      this.job.date,
    );
  }

  private updateTimeTrackingTotal() {
    const total = this.getTimeTrackingTotal();
    this.timeTrackingTotal$.next(total);
    this.cdr.detectChanges();
  }

  // Time tracking form methods for step 0
  private updateTimeTrackingFormStep0(
    timeTrackingsWithFormGroup: TimeTrackingWithFormGroup[],
  ) {
    if (
      !timeTrackingsWithFormGroup ||
      timeTrackingsWithFormGroup.length === 0
    ) {
      return;
    }

    // Ensure form array is initialized
    if (!this.timeTrackingFormsArrayStep0) {
      return;
    }

    // Clear existing form array
    this.timeTrackingFormsArrayStep0.clear();

    // Add the existing form groups to the form array
    timeTrackingsWithFormGroup.forEach((item) => {
      this.timeTrackingFormsArrayStep0.push(item.formGroup);
    });

    this.cdr.detectChanges();
  }

  canSubmitTimeTracking(): boolean {
    const timeTrackingsWithFormGroup = this.timeTrackingsWithFormGroup$.value;
    return (
      !timeTrackingsWithFormGroup.some(
        (tracking) => !!tracking.timeTracking.toBeApprovedAt,
      ) &&
      this.timeTrackingFormsArrayStep0.valid &&
      this.timeTrackingTotal >= 0
    );
  }
}
